/**
 * Money / decimal arithmetic toolkit.
 *
 * All amount-related arithmetic in the frontend MUST go through this module
 * (USDT balances, prices, micro-USDT API strings, credits, percentages, etc.).
 *
 * Hard rules (also enforced by `.cursor/rules/numeric-precision.mdc`):
 *
 *   1. Never apply `+ - * /` directly on amount-typed values; use `addUsdt`,
 *      `subUsdt`, `mulUsdt`, `divUsdt`.
 *   2. Never use `Math.floor / Math.ceil / Math.round * factor` to truncate
 *      decimals; use `floorToDecimals` / `formatUsdt`.
 *   3. Never use `Number(apiString)` then compare/arithmetic; use `M(value)`
 *      and the `gtUsdt / lteUsdt / ...` family.
 *   4. Never use `toFixed(n)` to truncate; it rounds. Use `floorToDecimals`.
 *   5. Never use `parseFloat / parseInt` on amount strings.
 *
 * Why decimal.js: the project deals with USDT (6 decimals) plus user-input
 * 4-decimal values, both can exceed `Number.MAX_SAFE_INTEGER` once expressed
 * in micro units, and binary float math (`0.1 + 0.2 !== 0.3`) directly causes
 * "deficit" / "available balance" UI bugs. decimal.js gives arbitrary-precision
 * decimal math with explicit rounding modes.
 */

import Decimal from 'decimal.js';

// Default precision (50 sig digits) is comfortably larger than any USDT amount
// the system can hold, and ROUND_DOWN matches the product spec ("truncate, do
// not round" for balance display / withdraw input clamping).
Decimal.set({
  precision: 50,
  rounding: Decimal.ROUND_DOWN,
});

/** Anything we accept as an amount input. */
export type MoneyInput = Decimal | string | number | bigint;

/** Decimal places used by the chain-side micro USDT representation (1 USDT = 1_000_000 micro). */
export const USDT_MICRO_DECIMALS = 6;

/** Default zero placeholder used by UI helpers (mirrors historical `formatUsdtBalance` behavior). */
export const USDT_ZERO_DISPLAY = '0.00';

/**
 * Strict decimal-literal pattern: optional sign, digits, optional fractional part.
 * Rejects scientific notation / hex / leading dots — anything we should not silently coerce.
 */
const DECIMAL_LITERAL = /^-?\d+(\.\d+)?$/;

/**
 * Try to coerce input into a finite Decimal. Returns null on:
 *  - empty / whitespace-only string
 *  - non-finite Decimal (NaN / Infinity)
 *  - throws from Decimal constructor (illegal literal)
 *
 * `number` inputs go through `String(n)` first to avoid Decimal interpreting
 * 0.1 as the binary float (decimal.js can but we want consistency).
 */
function tryDecimal(input: MoneyInput | null | undefined): Decimal | null {
  if (input === null || input === undefined) return null;
  if (input instanceof Decimal) {
    return input.isFinite() ? input : null;
  }
  try {
    let raw: string;
    if (typeof input === 'number') {
      if (!Number.isFinite(input)) return null;
      raw = String(input);
    } else if (typeof input === 'bigint') {
      raw = input.toString();
    } else {
      raw = input.trim();
      if (raw === '') return null;
    }
    const d = new Decimal(raw);
    return d.isFinite() ? d : null;
  } catch {
    return null;
  }
}

/**
 * Strict constructor: throws if `input` is not a finite numeric value.
 * Use when downstream code requires a definite Decimal (and a thrown error
 * surfaces a real bug). For "best-effort + fallback" parsing prefer `tryM`.
 */
export function M(input: MoneyInput): Decimal {
  const d = tryDecimal(input);
  if (d === null) {
    throw new TypeError(`[money] not a finite numeric value: ${String(input)}`);
  }
  return d;
}

/**
 * Lenient constructor: returns `null` instead of throwing on invalid input.
 * Useful at API boundaries where backend may return null / "" / NaN.
 */
export function tryM(input: MoneyInput | null | undefined): Decimal | null {
  return tryDecimal(input);
}

// ---------- Arithmetic ----------

export function addUsdt(a: MoneyInput, b: MoneyInput): string {
  return M(a).plus(M(b)).toString();
}

export function subUsdt(a: MoneyInput, b: MoneyInput): string {
  return M(a).minus(M(b)).toString();
}

export function mulUsdt(a: MoneyInput, b: MoneyInput): string {
  return M(a).times(M(b)).toString();
}

export function divUsdt(a: MoneyInput, b: MoneyInput): string {
  const divisor = M(b);
  if (divisor.isZero()) {
    throw new RangeError('[money] divUsdt: division by zero');
  }
  return M(a).div(divisor).toString();
}

// ---------- Comparison ----------

export function compareUsdt(a: MoneyInput, b: MoneyInput): -1 | 0 | 1 {
  const c = M(a).cmp(M(b));
  if (c < 0) return -1;
  if (c > 0) return 1;
  return 0;
}

export function gtUsdt(a: MoneyInput, b: MoneyInput): boolean {
  return M(a).gt(M(b));
}

export function gteUsdt(a: MoneyInput, b: MoneyInput): boolean {
  return M(a).gte(M(b));
}

export function ltUsdt(a: MoneyInput, b: MoneyInput): boolean {
  return M(a).lt(M(b));
}

export function lteUsdt(a: MoneyInput, b: MoneyInput): boolean {
  return M(a).lte(M(b));
}

export function eqUsdt(a: MoneyInput, b: MoneyInput): boolean {
  return M(a).eq(M(b));
}

export function maxUsdt(...xs: MoneyInput[]): string {
  if (xs.length === 0) {
    throw new RangeError('[money] maxUsdt: at least one argument required');
  }
  return Decimal.max(...xs.map((x) => M(x))).toString();
}

export function minUsdt(...xs: MoneyInput[]): string {
  if (xs.length === 0) {
    throw new RangeError('[money] minUsdt: at least one argument required');
  }
  return Decimal.min(...xs.map((x) => M(x))).toString();
}

/** Clamp a value to `>= 0`. Negative inputs become "0". */
export function clampNonNegative(value: MoneyInput): string {
  const d = M(value);
  return d.isNegative() ? '0' : d.toString();
}

// ---------- Unit conversion (USDT <-> micro) ----------

/**
 * Convert micro-units (e.g. backend `monthlyPriceUsdtMicro`) to a USDT decimal string.
 *
 * Default `decimals = 6` matches `1 USDT = 1_000_000 micro`. Trailing zeros are
 * preserved on the output (callers that need stripped zeros should pipe through
 * `formatUsdt`).
 *
 * Accepts both string and number; string is preferred for values exceeding 2^53.
 */
export function microToUsdt(
  micro: MoneyInput,
  decimals: number = USDT_MICRO_DECIMALS,
): string {
  const safeDecimals = normalizeDecimals(decimals);
  // Divide by 10^decimals using Decimal to keep arbitrary precision intact.
  return M(micro).div(new Decimal(10).pow(safeDecimals)).toFixed();
}

/**
 * Convert a USDT decimal string to micro-units (integer string).
 *
 * The result is guaranteed to be an integer string (no fractional part); any
 * sub-`10^-decimals` fraction in the input is truncated toward zero.
 */
export function usdtToMicro(
  usdt: MoneyInput,
  decimals: number = USDT_MICRO_DECIMALS,
): string {
  const safeDecimals = normalizeDecimals(decimals);
  return M(usdt)
    .times(new Decimal(10).pow(safeDecimals))
    .toDecimalPlaces(0, Decimal.ROUND_DOWN)
    .toFixed();
}

// ---------- Formatting / truncation ----------

/**
 * Truncate a value to at most `n` fractional digits (no rounding, ROUND_DOWN).
 * Returns a plain decimal string with trailing zeros preserved.
 */
export function floorToDecimals(value: MoneyInput, n: number): string {
  const safeN = normalizeDecimals(n);
  return M(value).toDecimalPlaces(safeN, Decimal.ROUND_DOWN).toFixed(safeN);
}

/** Significant digits kept on Tron Energy USDT display, counted from the first non-zero digit. */
export const USDT_SIGNIFICANT_DIGITS = 4;

export type FormatUsdtOptions = {
  /** Maximum fractional digits to keep (truncated, never rounded). Default 4. */
  maxDecimals?: number;
  /**
   * When set, keep this many significant digits from the first non-zero digit
   * (truncated, never rounded). Overrides `maxDecimals`.
   */
  significantDigits?: number;
  /** Strip trailing zeros (and trailing decimal point) from the output. Default true. */
  stripTrailingZeros?: boolean;
  /** Display string for null / NaN / 0 values. Default "0.00". */
  zeroDisplay?: string;
};

/**
 * Format a USDT amount for display.
 *
 * Behaviour (kept compatible with the legacy `formatUsdtBalance`):
 *   - Truncate to at most `maxDecimals` fractional digits (no rounding).
 *   - Negative values are truncated toward zero.
 *   - Trailing zeros are stripped by default; if no fractional digits remain
 *     the trailing dot is removed too. So `1.1` stays "1.1", `10` becomes
 *     "10", `1.2000` becomes "1.2".
 *   - Zero / null / undefined / NaN / Infinity render as `zeroDisplay`
 *     (default "0.00") so empty-balance UI stays visually consistent.
 *
 * @example
 *   formatUsdt("10.123999")        // "10.1239"
 *   formatUsdt(0)                   // "0.00"
 *   formatUsdt("0")                 // "0.00"
 *   formatUsdt(undefined)           // "0.00"
 *   formatUsdt(-1.23459)            // "-1.2345"
 *   formatUsdt("10.1000", { stripTrailingZeros: false }) // "10.1000"
 *   formatUsdt("0.00001632", { significantDigits: 4 })   // "0.00001632"
 *   formatUsdt("1.469673", { significantDigits: 4 })     // "1.469"
 */
export function formatUsdt(
  value: MoneyInput | null | undefined,
  options: FormatUsdtOptions = {},
): string {
  const {
    maxDecimals = 4,
    significantDigits,
    stripTrailingZeros = true,
    zeroDisplay = USDT_ZERO_DISPLAY,
  } = options;
  const d = tryDecimal(value);
  if (d === null || d.isZero()) {
    return zeroDisplay;
  }

  if (significantDigits != null) {
    const truncated = d.toSignificantDigits(
      normalizeSignificantDigits(significantDigits),
      Decimal.ROUND_DOWN,
    );
    if (truncated.isZero()) {
      return zeroDisplay;
    }
    // toFixed() without dp stays in normal notation (not scientific).
    const fixed = truncated.toFixed();
    return stripTrailingZeros ? stripTrailingZerosFromDecimalString(fixed) : fixed;
  }

  const safeDecimals = normalizeDecimals(maxDecimals);
  const truncated = d.toDecimalPlaces(safeDecimals, Decimal.ROUND_DOWN);
  if (truncated.isZero()) {
    return zeroDisplay;
  }
  const fixed = truncated.toFixed(safeDecimals);
  return stripTrailingZeros ? stripTrailingZerosFromDecimalString(fixed) : fixed;
}

/** Subscription plan price fields from GET /subscriptions/plans. */
export type SubscriptionPlanPriceFields = {
  monthlyPriceUsdt?: string | null;
  monthlyPriceUsdtMicro?: number | string | null;
  yearlyPriceUsdt?: string | null;
  yearlyPriceUsdtMicro?: number | string | null;
};

export type SubscriptionPlanBillingCycle = 'monthly' | 'yearly';

/**
 * Resolve the canonical USDT price string for a subscription plan.
 *
 * Prefers API `monthlyPriceUsdt`; falls back to micro conversion when the
 * string field is missing. Callers should use this (not ad-hoc regex stripping
 * on `microToUsdt`) before display or Decimal-safe comparisons.
 */
export function resolveSubscriptionPlanPriceUsdt(
  fields: SubscriptionPlanPriceFields,
  billingCycle: SubscriptionPlanBillingCycle = 'monthly',
): string {
  const priceField =
    billingCycle === 'yearly' ? fields.yearlyPriceUsdt : fields.monthlyPriceUsdt;
  const microField =
    billingCycle === 'yearly'
      ? fields.yearlyPriceUsdtMicro
      : fields.monthlyPriceUsdtMicro;

  const fromApi = tryM(priceField ?? undefined);
  if (fromApi !== null) {
    return fromApi.toString();
  }

  const microD = tryM(microField ?? undefined);
  return microD === null ? '0' : microToUsdt(microD, USDT_MICRO_DECIMALS);
}

/**
 * Format a subscription plan price for card / modal display.
 */
export function formatSubscriptionPlanPriceDisplay(
  fields: SubscriptionPlanPriceFields,
  billingCycle: SubscriptionPlanBillingCycle = 'monthly',
): string {
  return formatUsdt(resolveSubscriptionPlanPriceUsdt(fields, billingCycle), {
    maxDecimals: USDT_MICRO_DECIMALS,
    zeroDisplay: '0',
  });
}

export type FormatCompactNumberOptions = {
  /** BCP 47 locale for grouping the sub-million integer branch (e.g. "en-US"). */
  locale?: string;
  /** Maximum fractional digits kept on the M/B/T branch (truncated, never rounded). Default 2. */
  maxDecimals?: number;
};

/**
 * Format a (typically integer) count for compact display.
 *
 * Scheme:
 *   - `< 1,000,000`: full integer with locale grouping (e.g. 12345 -> "12,345").
 *   - `>= 1e6 / 1e9 / 1e12`: abbreviated with `M` / `B` / `T`, truncated (not
 *     rounded) to `maxDecimals` and trailing zeros stripped (e.g. 1234567 ->
 *     "1.23M", 2000000 -> "2M").
 *
 * Returns "0" for null / NaN / unparseable input.
 *
 * @example
 *   formatCompactNumber(850)                         // "850"
 *   formatCompactNumber(1234567, { locale: "en-US" }) // "1.23M"
 */
export function formatCompactNumber(
  value: MoneyInput | null | undefined,
  options: FormatCompactNumberOptions = {},
): string {
  const { locale, maxDecimals = 2 } = options;
  const d = tryDecimal(value);
  if (d === null) return '0';

  const abs = d.abs();
  const tiers = [
    { limit: '1000000000000', suffix: 'T' },
    { limit: '1000000000', suffix: 'B' },
    { limit: '1000000', suffix: 'M' },
  ];
  for (const tier of tiers) {
    if (abs.gte(tier.limit)) {
      const scaled = formatUsdt(divUsdt(d, tier.limit), {
        maxDecimals,
        zeroDisplay: '0',
      });
      return `${scaled}${tier.suffix}`;
    }
  }

  // < 1,000,000: grouped integer (display-only floor toward zero).
  const intValue = d.toDecimalPlaces(0, Decimal.ROUND_DOWN).toNumber();
  return new Intl.NumberFormat(locale).format(intValue);
}

// ---------- Validation ----------

/** Whether the input parses as a strictly positive finite amount. */
export function isPositiveUsdt(value: MoneyInput | null | undefined): boolean {
  const d = tryDecimal(value);
  return d !== null && d.isPositive() && !d.isZero();
}

/** Whether the input parses as a non-negative (>= 0) finite amount. */
export function isNonNegativeUsdt(value: MoneyInput | null | undefined): boolean {
  const d = tryDecimal(value);
  return d !== null && !d.isNegative();
}

/**
 * Whether `value` is a syntactically valid decimal literal (sign + digits + optional fraction).
 * Rejects scientific notation / hex / leading dots / empty strings.
 */
export function isValidUsdtNumeric(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL_LITERAL.test(value);
}

/**
 * Count the fractional digits of a decimal-literal string (e.g. "1.230" → 3).
 * Returns 0 for non-fractional inputs and -1 for unparseable inputs.
 */
export function countDecimals(value: MoneyInput | null | undefined): number {
  if (typeof value === 'string') {
    if (!DECIMAL_LITERAL.test(value)) return -1;
    const dot = value.indexOf('.');
    return dot === -1 ? 0 : value.length - dot - 1;
  }
  const d = tryDecimal(value);
  if (d === null) return -1;
  return Math.max(0, d.decimalPlaces());
}

// ---------- Internals ----------

function normalizeDecimals(n: number): number {
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

function normalizeSignificantDigits(n: number): number {
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1;
}

function stripTrailingZerosFromDecimalString(s: string): string {
  if (!s.includes('.')) return s;
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}
